import { useCallback, useEffect, useState } from "react";
import intl from "react-intl-universal";
import { TbAlertTriangle } from "react-icons/tb";
import {
  BAAS_VIEW_AS_PERSONAS,
  clearBaasViewAsPersona,
  readBaasViewAsPersona,
  setBaasViewAsPersona,
} from "../baasViewAs";

/**
 * Compact Valut-admin control in the app header: pick a persona overlay for the console.
 */
export const BaasViewAsButton = ({ className = "" }) => {
  const [open, setOpen] = useState(false);
  const [persona, setPersona] = useState(() => readBaasViewAsPersona());

  useEffect(() => {
    const onStorage = () => setPersona(readBaasViewAsPersona());
    window.addEventListener("storage", onStorage);
    window.addEventListener("baas-view-as-changed", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("baas-view-as-changed", onStorage);
    };
  }, []);

  const select = useCallback((id) => {
    setBaasViewAsPersona(id);
    setPersona(readBaasViewAsPersona());
    window.dispatchEvent(new Event("baas-view-as-changed"));
    setOpen(false);
  }, []);

  const exit = useCallback(() => {
    clearBaasViewAsPersona();
    setPersona(null);
    window.dispatchEvent(new Event("baas-view-as-changed"));
    setOpen(false);
  }, []);

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-8 max-w-full items-center whitespace-nowrap rounded-lg border border-gray-200 bg-white px-2 text-[0.65rem] font-semibold text-brand-dark transition hover:border-gray-300 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100 sm:h-9 sm:px-3 sm:text-xs"
      >
        {intl.get("baas.instances.viewAs.button")}
        {persona ? ` · ${intl.get(`baas.instances.viewAs.roles.${persona.id}`)}` : ""}
      </button>
      {open ? (
        <div className="absolute right-0 z-50 mt-1 w-64 rounded-xl border border-gray-200 bg-white p-2 text-left shadow-lg dark:border-brand-dark-border dark:bg-brand-dark-surface">
          <p className="px-2 pb-1 text-[11px] font-semibold text-gray-500 dark:text-gray-400">
            {intl.get("baas.instances.viewAs.title")}
          </p>
          <p className="px-2 pb-2 text-[10px] text-gray-400 dark:text-gray-500">
            {intl.get("baas.instances.viewAs.subtitle")}
          </p>
          <ul className="max-h-64 space-y-0.5 overflow-auto">
            {BAAS_VIEW_AS_PERSONAS.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => select(item.id)}
                  className={`flex w-full rounded-lg px-2 py-1.5 text-left text-xs transition hover:bg-gray-50 dark:hover:bg-brand-dark-elevated ${
                    persona?.id === item.id
                      ? "font-semibold text-[#b87a1a]"
                      : "text-brand-dark dark:text-gray-100"
                  }`}
                >
                  {intl.get(`baas.instances.viewAs.roles.${item.id}`)}
                </button>
              </li>
            ))}
          </ul>
          {persona ? (
            <button
              type="button"
              onClick={exit}
              className="mt-1 w-full rounded-lg px-2 py-1.5 text-left text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
            >
              {intl.get("baas.instances.viewAs.exit")}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Stripe-style amber alert when "Visualizar como" is active.
 * Full-bleed across PrivateApp horizontal padding.
 * Vertical gap to titles / rounded cards is owned by BaasConsoleChrome.
 * Pass flush when a parent sticky stack owns sticky/-mx chrome.
 */
export const BaasViewAsBanner = ({ flush = false }) => {
  const [persona, setPersona] = useState(() => readBaasViewAsPersona());

  useEffect(() => {
    const onStorage = () => setPersona(readBaasViewAsPersona());
    window.addEventListener("storage", onStorage);
    window.addEventListener("baas-view-as-changed", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("baas-view-as-changed", onStorage);
    };
  }, []);

  if (!persona) return null;

  const roleLabel = intl.get(`baas.instances.viewAs.roles.${persona.id}`);
  const layoutClass = flush
    ? "baas-view-as-banner flex items-center gap-3 border-b-2 border-amber-400 bg-amber-400 px-4 py-3 text-sm text-amber-950 shadow-sm dark:border-amber-500 dark:bg-amber-500 dark:text-amber-950 md:px-5 lg:px-8"
    : "baas-view-as-banner sticky top-0 z-30 -mx-4 flex items-center gap-3 border-b-2 border-amber-400 bg-amber-400 px-4 py-3 text-sm text-amber-950 shadow-sm dark:border-amber-500 dark:bg-amber-500 dark:text-amber-950 md:-mx-5 md:px-5 lg:-mx-8 lg:px-8";

  return (
    <div className={layoutClass} role="status" aria-live="polite">
      <TbAlertTriangle className="h-5 w-5 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1 font-semibold leading-snug">
        {intl.get("baas.instances.viewAs.banner", { role: roleLabel })}
      </p>
      <button
        type="button"
        onClick={() => {
          clearBaasViewAsPersona();
          setPersona(null);
          window.dispatchEvent(new Event("baas-view-as-changed"));
        }}
        className="shrink-0 rounded-md bg-amber-950/90 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-50 transition hover:bg-amber-950"
      >
        {intl.get("baas.instances.viewAs.exit")}
      </button>
    </div>
  );
};
